/**
 * @typedef {Object} MenuContext
 * @property {string[]} selectedElements - Array of selected element IDs from DOM dataset
 * @property {boolean} isSingleElement - Whether a single element is selected
 * @property {string|null} targetElementID - ID of the right-clicked element
 * @property {boolean} isLocked - Whether the target element (or its group) is locked
 * @property {boolean} isUnselectedBlock - Whether the right-click target is on an unselected block
 * @property {boolean} isUnselectedLayout - Whether the right-click target is on an unselected layout
 * @property {string|undefined} unselectedBlockId - Block ID if clicking on an unselected block
 * @property {string} layout - Current layout (desktop/mobile)
 * @property {string} otherLayout - The other layout
 * @property {boolean} isSiteBlock - Whether the selected block is a site block
 * @property {boolean} isSiteBlockInstance - Whether the selected block is a site block instance
 * @property {import("immutable").Map} selectedBlock - Immutable block object
 */

/**
 * @typedef {Object} MenuState
 * @property {"element"|"block"|"stateElement"} entityType - Type of entity that was right-clicked
 * @property {import("immutable").Map|null} entity - The target element or block data
 * @property {number} mouseX - Mouse X coordinate
 * @property {number} mouseY - Mouse Y coordinate
 * @property {MenuContext} context - Contextual information about the right-click target
 * @property {{ [layout: string]: { x: number, y: number } }} position - Canvas coordinates per layout
 * @property {{ targetedLayout: string, isUnselectedLayout: boolean }} layoutSwitching - Layout switching info
 */

import AppConstants from "@/constants/AppConstants";
import SiteDesignStore from "@/stores/SiteDesignStore";
import { getContainerData } from "@/utils/GroupUtils";
import IDUtils from "@/utils/IDUtils";

const {
  LAYOUT_MOBILE_WIDTH,
  LAYOUT_DESKTOP_WIDTH,
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  LOCK_EDITING_KEY,
  SITE_ITEM_SITE,
  DESIGN_ITEM_PROP_PARENT_TYPE,
} = AppConstants;

const layoutWidthMap = {
  [LAYOUT_DESKTOP]: LAYOUT_DESKTOP_WIDTH,
  [LAYOUT_MOBILE]: LAYOUT_MOBILE_WIDTH,
};

const EDITING_TEXT_ID = "text-element";
const UNSELECTED_CANVAS_STAGE_BACKGROUND_SUFFIX = "stage_bg";

/**
 * Determines if the context menu should be shown for the given target element
 * @param {string} targetID - The DOM element ID that was right-clicked
 * @returns {boolean} True if context menu should be shown
 */
export const shouldShowContextMenu = (targetID) => {
  return !(
    targetID.includes(EDITING_TEXT_ID) ||
    targetID.includes(UNSELECTED_CANVAS_STAGE_BACKGROUND_SUFFIX)
  );
};

/**
 * Detects if the right-clicked target is a transformer element
 * @param {MouseEvent} event - The right-click event
 * @returns {boolean} True if target is a transformer
 */
export const isTransformerTarget = (event) => {
  return event.target.classList.contains("transformer-outline");
};

export const getTargetDataset = (event) => {
  const isTransformer = isTransformerTarget(event);
  if (isTransformer) {
    const transformerParentDiv = event.target.closest("div");
    return { dataset: transformerParentDiv.dataset, isTransformer };
  }
  return { dataset: event.target.dataset, isTransformer };
};

export const detectTargetContext = (event, selectedBlock) => {
  const { dataset, isTransformer } = getTargetDataset(event);
  const { blockId, id: unSelectedElementId, selectedElements: selectedElementsString } = dataset;

  const selectedElements = selectedElementsString ? JSON.parse(selectedElementsString) : [];
  const selectedBlockId = selectedBlock.get("id");

  const isElement = unSelectedElementId || isTransformer;
  const isStateElement = isElement && event.target.id.includes("states_");
  const isUnselectedBlock = (blockId && blockId !== selectedBlockId) || !selectedBlockId;

  const targetType = isStateElement ? "stateElement" : isElement ? "element" : "block";

  return {
    targetType,
    isElement,
    isStateElement,
    isUnselectedBlock,
    selectedElements,
    unSelectedElementId,
    blockId,
  };
};

/**
 * Gets element context information for menu item conditions
 * @param {string} targetElementID - ID of the element that was right-clicked
 * @param {string} selectedBlockId - ID of the currently selected block
 * @param {string[]} selectedElements - Array of selected element IDs from DOM dataset
 * @param {Object} selectedBlock - Immutable block object from the store
 * @returns {Object} Element context with target element, lock status, and selection info
 */
export const getElementContext = (
  targetElementID,
  selectedBlockId,
  selectedElements,
  selectedBlock,
) => {
  if (!selectedBlockId) {
    return { targetElement: null, isLocked: false };
  }

  let elementId = targetElementID;
  let actualSelectedElements = selectedElements || [];

  // Fall back to selected elements for transformer cases
  if (!elementId) {
    if (actualSelectedElements.length > 0) {
      elementId = actualSelectedElements[0];
    } else if (selectedBlock) {
      const currentSelectedElements = selectedBlock.getIn(["selected", "elements"]);
      if (currentSelectedElements && currentSelectedElements.size > 0) {
        actualSelectedElements = currentSelectedElements.map((elem) => elem.id).toArray();
        elementId = actualSelectedElements[0];
      }
    }
  }

  if (!elementId) {
    return { targetElement: null, isLocked: false, isSingleElement: true, targetElementID: null };
  }

  const isSingleElement = !actualSelectedElements.length || actualSelectedElements.length <= 1;

  const targetElement = SiteDesignStore.getElementData(elementId, selectedBlockId)?.set(
    "id",
    elementId,
  );
  const block = SiteDesignStore.getBlock(selectedBlockId);
  const containerData = getContainerData(block, elementId);
  const isLocked =
    !!targetElement?.get(LOCK_EDITING_KEY) || containerData?.get(LOCK_EDITING_KEY) === "1";

  return {
    targetElement,
    isLocked,
    isSingleElement,
    targetElementID: elementId,
  };
};

export const getSiteBlockContext = (selectedBlock) => {
  const isSiteBlock =
    selectedBlock.get(DESIGN_ITEM_PROP_PARENT_TYPE) === SITE_ITEM_SITE ||
    selectedBlock.get("pBlockId") !== undefined;
  const isSiteBlockInstance = selectedBlock.get("pBlockId") !== undefined;

  return { isSiteBlock, isSiteBlockInstance };
};

/**
 * Converts window-space mouse coordinates to canvas-space coordinates relative to the block origin.
 * Also estimates the equivalent position in the other layout by proportional scaling.
 * @param {MouseEvent} event - Mouse event with clientX/clientY (window space)
 * @param {import("immutable").Map} selectedBlock - The currently selected block
 * @param {string} layout - Current layout (desktop/mobile)
 * @param {number} zoom - Current zoom level
 * @returns {{ position: { [layout: string]: { x: number, y: number } }, otherLayout: string }}
 *   Position values are in canvas space (block-relative, unzoomed)
 */
export const calculateCanvasPosition = (event, selectedBlock, layout, zoom) => {
  const selectedBlockId = selectedBlock.get("id");
  const blockDomId = IDUtils.getDOMID("si_block", layout, selectedBlockId);
  const blockElement = document.getElementById(blockDomId);
  const blockRect = blockElement?.getBoundingClientRect() || { left: 0, top: 0 };

  const canvasX = (event.clientX - blockRect.left) / zoom;
  const canvasY = (event.clientY - blockRect.top) / zoom;
  const otherLayout = layout === LAYOUT_DESKTOP ? LAYOUT_MOBILE : LAYOUT_DESKTOP;

  const bestGuessOtherLayoutX = (canvasX / layoutWidthMap[layout]) * layoutWidthMap[otherLayout];
  const bestGuessOtherLayoutY =
    (canvasY / selectedBlock.getIn([layout, "h"])) * selectedBlock.getIn([otherLayout, "h"]);

  return {
    position: {
      [layout]: { x: canvasX, y: canvasY },
      [otherLayout]: { x: bestGuessOtherLayoutX, y: bestGuessOtherLayoutY },
    },
    otherLayout,
  };
};

export const detectLayoutContext = (event, selectedBlock) => {
  const targetedLayout = event.target.closest("div[data-layout]")?.dataset?.layout;
  const isUnselectedLayout = targetedLayout !== selectedBlock.getIn(["selected", "layout"]);

  return {
    targetedLayout,
    isUnselectedLayout,
  };
};

/**
 * Main context detection function that analyzes a right-click event and returns
 * all necessary context information for menu rendering and positioning
 * @param {MouseEvent} event - The right-click event
 * @param {React.RefObject} stageCanvasRef - Reference to the stage canvas element
 * @param {Object} selectedBlock - Immutable block object from the store
 * @param {string} layout - Current layout (desktop/mobile)
 * @param {number} zoom - Current zoom level
 * @returns {Object|null} Complete context object or null if menu shouldn't show
 */
export const detectContext = (event, stageCanvasRef, selectedBlock, layout, zoom) => {
  if (!stageCanvasRef.current || !shouldShowContextMenu(event.target.id)) {
    return null;
  }

  // Layout context
  const { targetedLayout, isUnselectedLayout } = detectLayoutContext(event, selectedBlock);

  // Target context
  const targetContext = detectTargetContext(event, selectedBlock);
  const {
    targetType,
    isElement,
    isUnselectedBlock,
    selectedElements,
    unSelectedElementId,
    blockId,
  } = targetContext;

  // Element context
  const elementContext = isElement
    ? getElementContext(
        unSelectedElementId,
        selectedBlock.get("id"),
        selectedElements,
        selectedBlock,
      )
    : { targetElement: null, isLocked: false, isSingleElement: false, targetElementID: null };

  // Site block context
  const siteBlockContext = getSiteBlockContext(selectedBlock);

  // Canvas position
  const { position, otherLayout } = calculateCanvasPosition(event, selectedBlock, layout, zoom);

  return {
    entityType: targetType,
    entity: isElement ? elementContext.targetElement : selectedBlock,
    mouseX: event.clientX,
    mouseY: event.clientY,
    context: {
      selectedElements,
      isSingleElement: elementContext.isSingleElement,
      targetElementID: elementContext.targetElementID,
      isLocked: elementContext.isLocked,
      isUnselectedBlock,
      isUnselectedLayout,
      unselectedBlockId: blockId,
      layout,
      otherLayout,
      ...siteBlockContext,
      selectedBlock,
    },
    position,
    layoutSwitching: {
      targetedLayout,
      isUnselectedLayout,
    },
  };
};

// Default export for compatibility
const ContextDetectionService = {
  shouldShowContextMenu,
  isTransformerTarget,
  getTargetDataset,
  detectTargetContext,
  getElementContext,
  getSiteBlockContext,
  calculateCanvasPosition,
  detectLayoutContext,
  detectContext,
};

export default ContextDetectionService;
